import React from 'react';
import _ from 'lodash';
import Joi from 'joi-browser';
import MedicalExaminationApi from '../../../services/workers/medicalExamination/MedicalExaminationApi';
import MedicalExaminationService from '../../../services/workers/medicalExamination/MedicalExaminationService';
import SessionStorageService from '../../../services/utilities/SessionStorageService';

import { WorkerHeader } from '../header/WorkerHeader';
import MedicalExaminationForm from '../form/MedicalExaminationForm';
import Information from '../../modals/common/Information';
import Confirmation from '../../modals/common/Confirmation';
import ConfirmationSafetyDocItemDialog from '../dialog/ConfirmationSafetyDocItemDialog';
import { FetchCcusDescription } from '../../FetchCcusDescription';

import masterSlideLoader from '../../../utilities/masterSlideLoader';
import { messageUtility } from '../../../utilities/messageUtility';
import { validationByJoi } from '../../../utilities/commonValidation';
import {
  extractErrorMessage,
  getZendeskArticleUrl,
} from '../../../utilities/commonUtil';
import { isNullOrUndefinedOrEmptyStr } from '../../../utilities/stringUtil';
import { convertDate } from '../../../utilities/validateDateUtil';
import EventMg, { EventType } from '../../../utilities/EventManager';

import {
  ENV_VARS,
  WORKER_TAB,
  WORKER_PAGE_STATUS,
  DIALOG_STATUS,
  SAFETY_DOC_ITEM_VALIDATE,
  ZENDESK_LINK_IDS,
  FREQUENCY_LIST,
  CCUS_SPECIAL_MEDICAL_EXAMINATION,
  ZENDESK_URL_GUIDE,
} from '../../../utilities/constants';

import styles from '../../../styles/componentStyle/workerForm/workerForm.scss';
import { MoveLeftIcon } from '../../icons/MoveLeftIcon';

/**
 * じん肺健康診断など特殊診断系のstateはprefix以外のstateが共通
 * 初期生成用の関数。stateが共通ではなくなった場合は初期化関数の変更が必要
 */
const createShowFormInitState = prefix => {
  return {
    [`${prefix}_class`]: null,
    [`${prefix}_high_bp`]: null,
    [`${prefix}_hospital_name`]: null,
    [`${prefix}_low_bp`]: null,
    [`${prefix}_observation_flag`]: null,
    [`${prefix}_sugar_inspection`]: null,
    [`${prefix}_visual_acuity_left`]: null,
    [`${prefix}_visual_acuity_right`]: null,
    [`${prefix}_x_ray_observation`]: null,
    [`${prefix}_medical_examination_frequency`]: null,
    [`special_medical_examination_date_${prefix}`]: null,
  };
};

/**
 * 健康診断ページ
 * @props {string}companyId:会社ID
 * @props {string}workerId:作業員ID
 * @props {object}pageStatus:ページ情報 (新規・詳細・編集)
 * @props {Boolean}isAlreadyCcusConnect:CCUSと連携しているか？
 * @props {Boolean}isAgencyRegister:代理ログインか？
 * @props {string}linkedToCcusDate:CCUSと連携した時間
 * @props {function}callFetchCcusWorkersData:CCUS情報取得処理
 */
export class MedicalExamination extends React.Component {
  state = {
    header: {
      // 作業員基本情報ヘッダー用プロパティ
      LinkButtonObject: {}, // 作業員一覧ページに戻るリンク (内容はbuttonObject)
      buttonObject: {}, // ボタン内容 (内容はbuttonObject)
    },
    headerButtonObject: {
      // ヘッダーで必要なモデル (詳細はWorkerHeader.jsxを参照)
      label: data.defaultButton.label, // テキスト
      className: data.defaultButton.className, // クラス名
      onClick: null, // 処理
    },
    dialogStatus: DIALOG_STATUS.disabled.id, // ダイアログ表示用ステータス
    informationModal: {
      // インフォメーションモーダル用プロパティ (ボタン1つ)
      message: null, // モーダルダイアログのメッセージ
    },
    validErrorMessage: {}, // バリデーションエラーメッセージ
    // 健康診断情報格納用オブジェクト
    medicalExaminations: {
      showPneumoconiosis: false,
      showAsbestos: false,
      showOrganicSolvent: false,
      showSpecificChemicals: false,
      showIonization: false,
      showVibrationDisease: false,
      showHighPressure: false,
      showLead: false,
      showTetraalkylLead: false,
      showDecontamination: false,
      showDental: false,
    },
    ccus_info: {}, // CCUSから取得した情報を格納する
    comboBoxList: {
      relationship: {}, // 続柄情報を格納する
    }, // 続柄マスターデータ格納用リスト
    errorPropertyList: [],
    roanToChoseiFlg: false, // 労安からの遷移先が直接「作業員詳細」か、黒メニュー「作業員一覧」経由かを区別するフラグ
  };

  events = {};

  /**
   * 特殊健康診断の項目入力があるかチェックする
   * @param {meValues} 検証対象の健康診断情報保持オブジェクト
   * @param {targetName} 検証対象の特殊健康診断名
   * @returns {boolean}
   */
  isSpMEValueExist = (meValues, targetName) => {
    // 受診日
    if (
      !_.isEmpty(meValues[`special_medical_examination_date_${targetName}`])
    ) {
      return true;
    }
    // 受診病院
    if (!_.isEmpty(meValues[`${targetName}_hospital_name`])) {
      return true;
    }
    // 所見の有無（true/false形式で値を持っておりisEmptyだとfalseの場合に正常に判定できない）
    if (!_.isNil(meValues[`${targetName}_observation_flag`])) {
      return true;
    }
    // 管理区分
    if (!_.isEmpty(meValues[`${targetName}_class`])) {
      return true;
    }
    // 受診頻度
    if (
      targetName === 'pneumoconiosis' ||
      targetName === 'organic_solvent' ||
      targetName === 'specific_chemicals' ||
      targetName === 'lead' ||
      targetName === 'tetraalkyl_lead'
    ) {
      if (!_.isEmpty(meValues[`${targetName}_medical_examination_frequency`])) {
        return true;
      }
    }
    // 視力左
    if (!_.isEmpty(meValues[`${targetName}_visual_acuity_left`])) {
      return true;
    }
    // 視力右
    if (!_.isEmpty(meValues[`${targetName}_visual_acuity_right`])) {
      return true;
    }
    // 血圧上
    if (!_.isEmpty(meValues[`${targetName}_high_bp`])) {
      return true;
    }
    // 血圧下
    if (!_.isEmpty(meValues[`${targetName}_low_bp`])) {
      return true;
    }
    // X線所見
    if (!_.isEmpty(meValues[`${targetName}_x_ray_observation`])) {
      return true;
    }
    // 糖検査
    if (!_.isEmpty(meValues[`${targetName}_sugar_inspection`])) {
      return true;
    }
    return false;
  };

  constructor(props) {
    super(props);

    this.updatingFlag = true; // 多重更新防止Flag

    if (
      !(
        props.pageStatus === WORKER_PAGE_STATUS.CONFIRM ||
        props.pageStatus === WORKER_PAGE_STATUS.EDIT
      )
    ) {
      throw new Error('MedicalExamination.jsx constructor props.pageStatus');
    }
    if (_.isUndefined(props.workerId)) {
      throw new Error('MedicalExamination.jsx constructor props.workerId');
    }
    if (_.isUndefined(props.companyId)) {
      throw new Error('MedicalExamination.jsx constructor props.companyId');
    }

    this.events.validate = this.validate.bind(this);
    this.events.setValueMedicalExamination = this.setValueMedicalExamination.bind(
      this
    );
    this.events.setCCUSValuesHealth = this.setCCUSValuesHealth.bind(this);
    this.events.switchDisplay = this.switchDisplay.bind(this);
    this.events.showCcusInfoTranscriptionAgreementDialog = this.showCcusInfoTranscriptionAgreementDialog.bind(
      this
    );
    this.state.roanToChoseiFlg = props.roanToChoseiFlg;

    switch (this.props.pageStatus.ID) {
      case WORKER_PAGE_STATUS.CONFIRM.ID:
        this.state = this.createObjectForMoveToWorkerList(this.state);
        this.state = this.createConfirmPageHeaderButton(this.state);
        break;
      case WORKER_PAGE_STATUS.EDIT.ID:
        this.state = this.createObjectForMoveToWorkerList(this.state);
        this.state = this.createEditPageHeaderButton(this.state);
        break;
      default:
        break;
    }

    this.requiredColumns = [
      'visual_acuity_left',
      'visual_acuity_right',
      'high_bp',
      'low_bp',
      'medical_examination_date',
      'hospital_name',
      'x_ray_observation',
      'sugar_inspection',
      'observation',
      'assigned_due_to_illness_flag',
      'special_medical_examination_name',
      'pneumoconiosis_class',
      'asbestos_class',
      'vibration_disease_class',
      'organic_solvent_class',
      'ionization_class',
      'specific_chemicals_class',
      'high_pressure_class',
      'lead_class',
      'tetraalkyl_lead_class',
      'decontamination_class',
      'dental_class',
      'special_medical_examination_date',
      'special_medical_examination_date_pneumoconiosis',
      'special_medical_examination_date_asbestos',
      'special_medical_examination_date_vibration_disease',
      'special_medical_examination_date_organic_solvent',
      'special_medical_examination_date_ionization',
      'special_medical_examination_date_specific_chemicals',
      'special_medical_examination_date_high_pressure',
      'special_medical_examination_date_lead',
      'special_medical_examination_date_tetraalkyl_lead',
      'special_medical_examination_date_decontamination',
      'special_medical_examination_date_dental',
      'pneumoconiosis_hospital_name',
      'asbestos_hospital_name',
      'organic_solvent_hospital_name',
      'specific_chemicals_hospital_name',
      'ionization_hospital_name',
      'vibration_disease_hospital_name',
      'high_pressure_hospital_name',
      'lead_hospital_name',
      'tetraalkyl_lead_hospital_name',
      'decontamination_hospital_name',
      'dental_hospital_name',
      'pneumoconiosis_observation_flag',
      'asbestos_observation_flag',
      'organic_solvent_observation_flag',
      'specific_chemicals_observation_flag',
      'ionization_observation_flag',
      'vibration_disease_observation_flag',
      'high_pressure_observation_flag',
      'lead_observation_flag',
      'tetraalkyl_lead_observation_flag',
      'decontamination_observation_flag',
      'dental_observation_flag',
      'pneumoconiosis_visual_acuity_left',
      'pneumoconiosis_visual_acuity_right',
      'asbestos_visual_acuity_left',
      'asbestos_visual_acuity_right',
      'organic_solvent_visual_acuity_left',
      'organic_solvent_visual_acuity_right',
      'specific_chemicals_visual_acuity_left',
      'specific_chemicals_visual_acuity_right',
      'ionization_visual_acuity_left',
      'ionization_visual_acuity_right',
      'vibration_disease_visual_acuity_left',
      'vibration_disease_visual_acuity_right',
      'high_pressure_visual_acuity_left',
      'high_pressure_visual_acuity_right',
      'lead_visual_acuity_left',
      'lead_visual_acuity_right',
      'tetraalkyl_lead_visual_acuity_left',
      'tetraalkyl_lead_visual_acuity_right',
      'decontamination_visual_acuity_left',
      'decontamination_visual_acuity_right',
      'dental_visual_acuity_left',
      'dental_visual_acuity_right',
      'pneumoconiosis_high_bp',
      'pneumoconiosis_low_bp',
      'asbestos_high_bp',
      'asbestos_low_bp',
      'organic_solvent_high_bp',
      'organic_solvent_low_bp',
      'specific_chemicals_high_bp',
      'specific_chemicals_low_bp',
      'ionization_high_bp',
      'ionization_low_bp',
      'vibration_disease_high_bp',
      'vibration_disease_low_bp',
      'high_pressure_high_bp',
      'high_pressure_low_bp',
      'lead_high_bp',
      'lead_low_bp',
      'tetraalkyl_lead_high_bp',
      'tetraalkyl_lead_low_bp',
      'decontamination_high_bp',
      'decontamination_low_bp',
      'dental_high_bp',
      'dental_low_bp',
      'pneumoconiosis_x_ray_observation',
      'asbestos_x_ray_observation',
      'organic_solvent_x_ray_observation',
      'specific_chemicals_x_ray_observation',
      'ionization_x_ray_observation',
      'vibration_disease_x_ray_observation',
      'high_pressure_x_ray_observation',
      'lead_x_ray_observation',
      'tetraalkyl_lead_x_ray_observation',
      'decontamination_x_ray_observation',
      'dental_x_ray_observation',
      'pneumoconiosis_sugar_inspection',
      'asbestos_sugar_inspection',
      'organic_solvent_sugar_inspection',
      'specific_chemicals_sugar_inspection',
      'ionization_sugar_inspection',
      'vibration_disease_sugar_inspection',
      'high_pressure_sugar_inspection',
      'lead_sugar_inspection',
      'tetraalkyl_lead_sugar_inspection',
      'decontamination_sugar_inspection',
      'dental_sugar_inspection',
      'pneumoconiosis_medical_examination_frequency',
      'lead_medical_examination_frequency',
      'organic_solvent_medical_examination_frequency',
      'specific_chemicals_medical_examination_frequency',
      'tetraalkyl_lead_medical_examination_frequency',
    ];
    this.validationColumns = [
      'visual_acuity_left',
      'visual_acuity_right',
      'high_bp',
      'low_bp',
      'medical_examination_date',
      'special_medical_examination_date',
      'special_medical_examination_date_pneumoconiosis',
      'special_medical_examination_date_asbestos',
      'special_medical_examination_date_vibration_disease',
      'special_medical_examination_date_organic_solvent',
      'special_medical_examination_date_ionization',
      'special_medical_examination_date_specific_chemicals',
      'special_medical_examination_date_high_pressure',
      'special_medical_examination_date_lead',
      'special_medical_examination_date_tetraalkyl_lead',
      'special_medical_examination_date_decontamination',
      'special_medical_examination_date_dental',
      'pneumoconiosis_visual_acuity_left',
      'pneumoconiosis_visual_acuity_right',
      'asbestos_visual_acuity_left',
      'asbestos_visual_acuity_right',
      'organic_solvent_visual_acuity_left',
      'organic_solvent_visual_acuity_right',
      'specific_chemicals_visual_acuity_left',
      'specific_chemicals_visual_acuity_right',
      'ionization_visual_acuity_left',
      'ionization_visual_acuity_right',
      'vibration_disease_visual_acuity_left',
      'vibration_disease_visual_acuity_right',
      'high_pressure_visual_acuity_left',
      'high_pressure_visual_acuity_right',
      'lead_visual_acuity_left',
      'lead_visual_acuity_right',
      'tetraalkyl_lead_visual_acuity_left',
      'tetraalkyl_lead_visual_acuity_right',
      'decontamination_visual_acuity_left',
      'decontamination_visual_acuity_right',
      'dental_visual_acuity_left',
      'dental_visual_acuity_right',
      'pneumoconiosis_high_bp',
      'pneumoconiosis_low_bp',
      'asbestos_high_bp',
      'asbestos_low_bp',
      'organic_solvent_high_bp',
      'organic_solvent_low_bp',
      'specific_chemicals_high_bp',
      'specific_chemicals_low_bp',
      'ionization_high_bp',
      'ionization_low_bp',
      'vibration_disease_high_bp',
      'vibration_disease_low_bp',
      'high_pressure_high_bp',
      'high_pressure_low_bp',
      'lead_high_bp',
      'lead_low_bp',
      'tetraalkyl_lead_high_bp',
      'tetraalkyl_lead_low_bp',
      'decontamination_high_bp',
      'decontamination_low_bp',
      'dental_high_bp',
      'dental_low_bp',
    ];
  }

  /**
   * React Component LifeCycle
   * マウントが行われた直後
   * */
  async componentDidMount() {
    const state = _.cloneDeep(this.state);
    await MedicalExaminationApi.fetchMedicalExamination(
      this.props.companyId,
      this.props.workerId
    ).then(response => {
      if (response.result) {
        state.medicalExaminations = Object.assign(
          response.data.entities.worker_medical_examinations,
          response.data.entities.worker_special_medical_examinations
        );
        state.ccus_info = Object.assign(
          response.data.entities.ccus_worker_medical_examinations,
          response.data.entities.ccus_worker_special_medical_examinations
        );
        // 特殊健康診断の入力状況に応じて初期表示状態を変更
        state.medicalExaminations.showPneumoconiosis = this.isSpMEValueExist(
          state.medicalExaminations,
          'pneumoconiosis'
        );
        state.medicalExaminations.showAsbestos = this.isSpMEValueExist(
          state.medicalExaminations,
          'asbestos'
        );
        state.medicalExaminations.showOrganicSolvent = this.isSpMEValueExist(
          state.medicalExaminations,
          'organic_solvent'
        );
        state.medicalExaminations.showSpecificChemicals = this.isSpMEValueExist(
          state.medicalExaminations,
          'specific_chemicals'
        );
        state.medicalExaminations.showIonization = this.isSpMEValueExist(
          state.medicalExaminations,
          'ionization'
        );
        state.medicalExaminations.showVibrationDisease = this.isSpMEValueExist(
          state.medicalExaminations,
          'vibration_disease'
        );
        state.medicalExaminations.showHighPressure = this.isSpMEValueExist(
          state.medicalExaminations,
          'high_pressure'
        );
        state.medicalExaminations.showLead = this.isSpMEValueExist(
          state.medicalExaminations,
          'lead'
        );
        state.medicalExaminations.showTetraalkylLead = this.isSpMEValueExist(
          state.medicalExaminations,
          'tetraalkyl_lead'
        );
        state.medicalExaminations.showDecontamination = this.isSpMEValueExist(
          state.medicalExaminations,
          'decontamination'
        );
        state.medicalExaminations.showDental = this.isSpMEValueExist(
          state.medicalExaminations,
          'dental'
        );
        if (this.props.pageStatus.ID === WORKER_PAGE_STATUS.EDIT.ID) {
          _.each(FREQUENCY_LIST, (item, key) => {
            if (isNullOrUndefinedOrEmptyStr(state.medicalExaminations[key])) {
              state.medicalExaminations[key] = item[0].value;
            }
          });
        }
      }
    });

    // CCUSから情報を取得ボタンを押された後の処理
    if (this.props.pageStatus.ID === WORKER_PAGE_STATUS.EDIT.ID) {
      EventMg.addEventListener(EventType.FETCH_CCUS_INFO, async () => {
        // CCUS情報の再取得
        await MedicalExaminationApi.fetchMedicalExamination(
          this.props.companyId,
          this.props.workerId
        ).then(response => {
          if (response.result) {
            const state = _.cloneDeep(this.state);
            state.ccus_info = Object.assign(
              response.data.entities.ccus_worker_medical_examinations,
              response.data.entities.ccus_worker_special_medical_examinations
            );
            this.setState(state);
          }
        });
      });
    }

    this.setState(state, () => {
      this.updatingFlag = false; // 画面ロード後更新可能になる
    });
  }

  /**
   * React Component LifeCycle
   * コンポーネントがアンマウントされる時
   * */
  componentWillUnmount() {
    // アンマウントされる時にEventListenerを削除する
    EventMg.deleteEvent(EventType.FETCH_CCUS_INFO);
  }

  /**
   * =====================================================
   * ヘッダー周りの処理 - START
   * =====================================================
   */

  /**
   * buttonObjectを初期化する
   * @param {object} state: ステート
   * */
  clearButtonObject = state => {
    state.headerButtonObject.label = data.defaultButton.label;
    state.headerButtonObject.className = data.defaultButton.className;
    state.headerButtonObject.onClick = null;
    return state;
  };

  /**
   * 作業員一覧ページに戻るためのリンクを作成する
   * @param {object} state: ステート
   * */
  createObjectForMoveToWorkerList = state => {
    state.headerButtonObject.label = data.buttonLink.label;
    state.headerButtonObject.className = data.buttonLink.defaultClassName;
    state.headerButtonObject.onClick = this.handlerMoveToWorkerList.bind(this);
    state.header.LinkButtonObject.moveToWorkerList = _.cloneDeep(
      state.headerButtonObject
    );
    state = this.clearButtonObject(state);
    return state;
  };

  /**
   * 詳細画面時ボタンを作成
   * @param {object} state: ステート
   * */
  createConfirmPageHeaderButton = state => {
    // 編集ボタン作成
    state.headerButtonObject.label = data.header.confirm.button.edit.label;
    state.headerButtonObject.className =
      data.header.confirm.button.edit.className;
    state.headerButtonObject.onClick = this.moveToWorkerEdit.bind(this);
    state.header.buttonObject.cancel = _.cloneDeep(state.headerButtonObject);
    state = this.clearButtonObject(state);
    return state;
  };

  /**
   * 編集画面時ボタンを作成
   * @param {object} state: ステート
   * */
  createEditPageHeaderButton = state => {
    // 編集をやめるボタン作成
    state.headerButtonObject.label = data.header.edit.button.cancel.label;
    state.headerButtonObject.className =
      data.header.edit.button.cancel.className;
    state.headerButtonObject.onClick = this.showCancelEditDialog.bind(this);
    state.header.buttonObject.cancel = _.cloneDeep(state.headerButtonObject);
    state = this.clearButtonObject(state);

    // 更新ボタン作成
    state.headerButtonObject.label = data.header.edit.button.update.label;
    state.headerButtonObject.className =
      data.header.edit.button.update.className;
    state.headerButtonObject.onClick = this.handlerUpdate;
    state.header.buttonObject.register = _.cloneDeep(state.headerButtonObject);
    state = this.clearButtonObject(state);
    return state;
  };

  /**
   * 作業員一覧へ戻るリンクをクリックした時の処理
   * */
  handlerMoveToWorkerList = () => {
    switch (this.props.pageStatus.ID) {
      case WORKER_PAGE_STATUS.EDIT.ID:
        this.showLeaveThisPageDialog();
        break;
      case WORKER_PAGE_STATUS.CONFIRM.ID:
        this.moveToWorkerList();
        break;
      default:
        break;
    }
  };

  /**
   * =====================================================
   * ヘッダー周りの処理 - END
   * =====================================================
   */

  /**
   * =====================================================
   * 画面遷移周りの処理 - START
   * =====================================================
   */

  /**
   * 作業員一覧画面に遷移する
   */
  moveToWorkerList = () => {
    masterSlideLoader.show('Workers', { state: {} });
  };

  /**
   * 詳細画面に遷移する
   */
  moveToWorkerConfirm = () => {
    if (this.state.roanToChoseiFlg) {
      SessionStorageService.setRoanToChoseiFlg(this.state.roanToChoseiFlg);
    }
    masterSlideLoader.show('WorkerConfirm', {
      state: {
        id: `${WORKER_TAB.MEDICAL_EXAMINATION.TAB_NAME},${this.props.workerId}`,
      },
    });
  };

  /**
   * 編集画面に遷移する
   */
  moveToWorkerEdit = () => {
    if (this.state.roanToChoseiFlg) {
      SessionStorageService.setRoanToChoseiFlg(this.state.roanToChoseiFlg);
    }
    masterSlideLoader.show('WorkerEdit', {
      state: {
        id: `${WORKER_TAB.MEDICAL_EXAMINATION.TAB_NAME},${this.props.workerId}`,
      },
    });
  };

  /**
   * =====================================================
   * 画面遷移周りの処理 - END
   * =====================================================
   */

  /**
   * =====================================================
   * ダイアログ周りの処理 - START
   * =====================================================
   */

  /**
   * ダイアログを閉じる
   * */
  closeDialog = () => {
    const state = _.cloneDeep(this.state);
    state.dialogStatus = DIALOG_STATUS.disabled.id;
    state.informationModal.message = null;
    this.setState(state);
  };

  /**
   * 編集をやめるダイアログを表示する
   * */
  showCancelEditDialog = () => {
    const state = _.cloneDeep(this.state);
    state.dialogStatus = DIALOG_STATUS.cancelEditDialogEnabled.id;
    this.setState(state);
  };

  /**
   * ページを移動する旨を示すダイアログを表示する
   * */
  showLeaveThisPageDialog = () => {
    const state = _.cloneDeep(this.state);
    state.dialogStatus = DIALOG_STATUS.leaveThisPageDialogEnabled.id;
    this.setState(state);
  };

  /**
   * CCUS情報をすべて転記することに同意するかを確認するダイアログを表示する
   * */
  showCcusInfoTranscriptionAgreementDialog = () => {
    const state = _.cloneDeep(this.state);
    state.dialogStatus =
      DIALOG_STATUS.ccusInfoTranscriptionAgreementDialogEnabled.id;
    this.setState(state);
  };

  /**
   * CCUS情報をすべて転記することに同意するかを確認するダイアログを閉じる
   * */
  closeCcusInfoTranscriptionAgreementDialog = () => {
    const state = _.cloneDeep(this.state);
    state.dialogStatus = DIALOG_STATUS.disabled.id;
    this.setState(state, () => this.setCCUSValuesHealth());
  };

  /**
   * =====================================================
   * ダイアログ周りの処理 - END
   * =====================================================
   */

  /**
   * =====================================================
   * API周りの処理 - START
   * =====================================================
   */

  getStateWithConvertedDate = () => {
    const state = _.cloneDeep(this.state);
    state.medicalExaminations.medical_examination_date = convertDate(
      state.medicalExaminations.medical_examination_date
    );
    state.medicalExaminations.special_medical_examination_date = convertDate(
      state.medicalExaminations.special_medical_examination_date
    );
    state.medicalExaminations.special_medical_examination_date_asbestos = convertDate(
      state.medicalExaminations.special_medical_examination_date_asbestos
    );
    state.medicalExaminations.special_medical_examination_date_ionization = convertDate(
      state.medicalExaminations.special_medical_examination_date_ionization
    );
    state.medicalExaminations.special_medical_examination_date_organic_solvent = convertDate(
      state.medicalExaminations.special_medical_examination_date_organic_solvent
    );
    state.medicalExaminations.special_medical_examination_date_pneumoconiosis = convertDate(
      state.medicalExaminations.special_medical_examination_date_pneumoconiosis
    );
    state.medicalExaminations.special_medical_examination_date_vibration_disease = convertDate(
      state.medicalExaminations
        .special_medical_examination_date_vibration_disease
    );
    state.medicalExaminations.special_medical_examination_date_specific_chemicals = convertDate(
      state.medicalExaminations
        .special_medical_examination_date_specific_chemicals
    );
    state.medicalExaminations.special_medical_examination_date_high_pressure = convertDate(
      state.medicalExaminations.special_medical_examination_date_high_pressure
    );
    state.medicalExaminations.special_medical_examination_date_lead = convertDate(
      state.medicalExaminations.special_medical_examination_date_lead
    );
    state.medicalExaminations.special_medical_examination_date_tetraalkyl_lead = convertDate(
      state.medicalExaminations.special_medical_examination_date_tetraalkyl_lead
    );
    state.medicalExaminations.special_medical_examination_date_decontamination = convertDate(
      state.medicalExaminations.special_medical_examination_date_decontamination
    );
    state.medicalExaminations.special_medical_examination_date_dental = convertDate(
      state.medicalExaminations.special_medical_examination_date_dental
    );
    return state;
  };

  /**
   * 健康診断情報更新
   */
  updateMedicalExamination() {
    // 多重更新防止
    if (this.updatingFlag === true) {
      return;
    }
    this.updatingFlag = true;

    const state = this.getStateWithConvertedDate();
    if (this.validateAll()) {
      const params = MedicalExaminationService.createUpdateMedicalExaminationParam(
        state.medicalExaminations
      );
      MedicalExaminationApi.updateMedicalExamination(
        this.props.companyId,
        this.props.workerId,
        this.clearSpecialMedicalExaminationsStateByCondition(params)
      )
        .then(response => {
          if (response.result === true) {
            this.updatingFlag = false;
            this.moveToWorkerConfirm();
            return;
          }

          state.dialogStatus = DIALOG_STATUS.updateFailedDialogEnabled.id;
          state.informationModal.message = extractErrorMessage(response);
          this.updatingFlag = false;
          this.setState(state);
        })
        .catch(error => {
          state.dialogStatus = DIALOG_STATUS.updateFailedDialogEnabled.id;
          state.informationModal.message = extractErrorMessage(error);
          this.updatingFlag = false;
          this.setState(state);
        });
    }
  }

  // 特殊健康診断の入力するがチェックされていない時、関連する値を初期値にして返す
  clearSpecialMedicalExaminationsStateByCondition = formValues => {
    const initSorkerSpecialMedicalExaminationsState = (result, type) => {
      return {
        ...result,
        worker_special_medical_examinations: {
          ...result.worker_special_medical_examinations,
          ...createShowFormInitState(type),
        },
      };
    };
    const {
      showPneumoconiosis,
      showAsbestos,
      showOrganicSolvent,
      showSpecificChemicals,
      showIonization,
      showVibrationDisease,
      showHighPressure,
      showLead,
      showTetraalkylLead,
      showDecontamination,
      showDental,
    } = formValues.worker_special_medical_examinations;

    let result = {
      ...formValues,
    };

    if (!showPneumoconiosis) {
      result = initSorkerSpecialMedicalExaminationsState(
        result,
        'pneumoconiosis'
      );
    }
    if (!showAsbestos) {
      result = initSorkerSpecialMedicalExaminationsState(result, 'asbestos');
    }
    if (!showOrganicSolvent) {
      result = initSorkerSpecialMedicalExaminationsState(
        result,
        'organic_solvent'
      );
    }
    if (!showSpecificChemicals) {
      result = initSorkerSpecialMedicalExaminationsState(
        result,
        'specific_chemicals'
      );
    }
    if (!showIonization) {
      result = initSorkerSpecialMedicalExaminationsState(result, 'ionization');
    }
    if (!showVibrationDisease) {
      result = initSorkerSpecialMedicalExaminationsState(
        result,
        'vibration_disease'
      );
    }
    if (!showHighPressure) {
      result = initSorkerSpecialMedicalExaminationsState(
        result,
        'high_pressure'
      );
    }
    if (!showLead) {
      result = initSorkerSpecialMedicalExaminationsState(result, 'lead');
    }
    if (!showTetraalkylLead) {
      result = initSorkerSpecialMedicalExaminationsState(
        result,
        'tetraalkyl_lead'
      );
    }
    if (!showDecontamination) {
      result = initSorkerSpecialMedicalExaminationsState(
        result,
        'decontamination'
      );
    }
    if (!showDental) {
      result = initSorkerSpecialMedicalExaminationsState(result, 'dental');
    }
    return result;
  };

  /**
   * =====================================================
   * API周りの処理 - END
   * =====================================================
   */

  /**
   * =====================================================
   *フォーム周りの処理 - START
   * =====================================================
   */

  /**
   * 健康診断の情報をセット
   * @param {string} keyName：セット対象のキー名
   * @param {string} value：セットするデータ
   */
  setValueMedicalExamination = (keyName, value) => {
    const state = _.cloneDeep(this.state);
    state.medicalExaminations[keyName] = value;
    this.setState(state);
  };

  /**
   * CCUS情報を転記する
   * @param {string} keyName 転記対象のキー名 指定がない場合は全項目を転記する
   */
  setCCUSValuesHealth = keyName => {
    const state = _.cloneDeep(this.state);

    if (isNullOrUndefinedOrEmptyStr(keyName) === false) {
      let val = state.ccus_info[keyName];
      if (isNullOrUndefinedOrEmptyStr(val) === false) {
        const ccusList = _.filter(
          CCUS_SPECIAL_MEDICAL_EXAMINATION,
          it => it.CCUS_NAME === keyName
        );
        if (ccusList.length > 0) {
          state.medicalExaminations[ccusList[0].CHECK_PROPERTY_NAME] = true;
        }
        state.medicalExaminations[keyName] = val;
      }
    } else {
      _.map(state.ccus_info, (val, key) => {
        if (isNullOrUndefinedOrEmptyStr(val) === false) {
          const ccusList = _.filter(
            CCUS_SPECIAL_MEDICAL_EXAMINATION,
            it => it.CCUS_NAME === key
          );
          if (ccusList.length > 0) {
            state.medicalExaminations[ccusList[0].CHECK_PROPERTY_NAME] = true;
          }

          state.medicalExaminations[key] = val;
        }
      });
    }

    this.setState(state, () => {
      if (!isNullOrUndefinedOrEmptyStr(keyName)) {
        this.validate(keyName);
      } else {
        this.validate('medical_examination_date');
      }
    });
  };

  /**
   * 特殊健康診断の各項目の表示/非表示を切り替える
   */
  switchDisplay = e => {
    const medicalExaminations = _.cloneDeep(this.state.medicalExaminations);
    const key = `show${e.target.name}`;
    medicalExaminations[key] = !medicalExaminations[key];
    this.setState({ medicalExaminations }, () => {
      this.validateAll();
    });
  };

  /**
   * 個別バリデーション
   * @param {string} keyName
   * @return {boolean} バリデート結果
   */
  validate = keyName => {
    // console.log('MedicalExamination.jsx validate', keyName);

    if (_.isUndefined(keyName)) {
      throw new Error('MedicalExamination.jsx validate');
    }
    if (!this.requiredColumns.includes(keyName)) {
      throw new Error(`MedicalExamination.jsx validate:${keyName}`);
    }
    if (!this.validationColumns.includes(keyName)) {
      return true;
      // 上記列挙した keyName 以外のものは
      // バリデート処理がないのにこの処理が呼ばれている。
      // 不要なので、returnする。
    }

    const stateValue = _.cloneDeep(this.state);
    const schema = MedicalExaminationService.createValidateSchema();

    const result = validationByJoi(
      Joi.object().keys(schema),
      stateValue.medicalExaminations
    );

    switch (keyName) {
      case 'visual_acuity_left':
      case 'visual_acuity_right':
      case 'pneumoconiosis_visual_acuity_left':
      case 'pneumoconiosis_visual_acuity_right':
      case 'asbestos_visual_acuity_left':
      case 'asbestos_visual_acuity_right':
      case 'organic_solvent_visual_acuity_left':
      case 'organic_solvent_visual_acuity_right':
      case 'specific_chemicals_visual_acuity_left':
      case 'specific_chemicals_visual_acuity_right':
      case 'ionization_visual_acuity_left':
      case 'ionization_visual_acuity_right':
      case 'vibration_disease_visual_acuity_left':
      case 'vibration_disease_visual_acuity_right':
      case 'high_pressure_visual_acuity_left':
      case 'high_pressure_visual_acuity_right':
      case 'lead_visual_acuity_left':
      case 'lead_visual_acuity_right':
      case 'tetraalkyl_lead_visual_acuity_left':
      case 'tetraalkyl_lead_visual_acuity_right':
      case 'decontamination_visual_acuity_left':
      case 'decontamination_visual_acuity_right':
      case 'dental_visual_acuity_left':
      case 'dental_visual_acuity_right':
        stateValue.validErrorMessage[keyName] = _.isUndefined(
          result.messages[keyName]
        )
          ? ''
          : messageUtility.getMessage('E-000-041');
        break;
      case 'high_bp':
      case 'low_bp':
      case 'pneumoconiosis_high_bp':
      case 'pneumoconiosis_low_bp':
      case 'asbestos_high_bp':
      case 'asbestos_low_bp':
      case 'organic_solvent_high_bp':
      case 'organic_solvent_low_bp':
      case 'specific_chemicals_high_bp':
      case 'specific_chemicals_low_bp':
      case 'ionization_high_bp':
      case 'ionization_low_bp':
      case 'vibration_disease_high_bp':
      case 'vibration_disease_low_bp':
      case 'high_pressure_high_bp':
      case 'high_pressure_low_bp':
      case 'lead_high_bp':
      case 'lead_low_bp':
      case 'tetraalkyl_lead_high_bp':
      case 'tetraalkyl_lead_low_bp':
      case 'decontamination_high_bp':
      case 'decontamination_low_bp':
      case 'dental_high_bp':
      case 'dental_low_bp':
        stateValue.validErrorMessage[keyName] = _.isUndefined(
          result.messages[keyName]
        )
          ? ''
          : messageUtility.getMessage('E-000-017');
        break;
      case 'medical_examination_date':
        stateValue.validErrorMessage.medical_examination_date_error = _.isUndefined(
          result.messages.medical_examination_date
        )
          ? ''
          : result.messages.medical_examination_date;
        break;
      case 'special_medical_examination_date':
      case 'special_medical_examination_date_asbestos':
      case 'special_medical_examination_date_ionization':
      case 'special_medical_examination_date_organic_solvent':
      case 'special_medical_examination_date_pneumoconiosis':
      case 'special_medical_examination_date_vibration_disease':
      case 'special_medical_examination_date_specific_chemicals':
      case 'special_medical_examination_date_high_pressure':
      case 'special_medical_examination_date_lead':
      case 'special_medical_examination_date_tetraalkyl_lead':
      case 'special_medical_examination_date_decontamination':
      case 'special_medical_examination_date_dental':
        stateValue.validErrorMessage[keyName] = _.isUndefined(
          result.messages[keyName]
        )
          ? ''
          : result.messages[keyName];
        break;
      default:
        break;
    }

    if (result.status) {
      stateValue.validErrorMessage = {};
    }
    this.setState(stateValue);
    return result.status;
  };

  resetErrorMessageStateByPrefix = (
    prefix,
    stateValidErrorMessage,
    validateResultMessages
  ) => {
    const stateMessage = _.cloneDeep(stateValidErrorMessage);
    const validMessage = _.cloneDeep(validateResultMessages);

    // バリデーションエラーとなる可能性がある項目
    const target = [
      `special_medical_examination_date_${prefix}`,
      `${prefix}_high_bp`,
      `${prefix}_low_bp`,
      `${prefix}_visual_acuity_left`,
      `${prefix}_visual_acuity_right`,
    ];

    for (const item of target) {
      stateMessage[item] = _.isUndefined(validMessage[item])
        ? ''
        : validMessage[item];
    }

    return stateMessage;
  };

  /**
   * バリデーション
   * @param {string} keyName
   * @return {boolean} バリデート結果
   *  validateAll の前に onBlur で validate が必ず実行されるために
   *  メッセージ表示処理はいらないような気もするが念の為実装する
   */
  validateAll = () => {
    const stateValue = _.cloneDeep(this.state);
    const schema = MedicalExaminationService.createValidateSchema();

    const result = validationByJoi(
      Joi.object().keys(schema),
      stateValue.medicalExaminations
    );

    stateValue.validErrorMessage.visual_acuity_left = _.isUndefined(
      result.messages.visual_acuity_left
    )
      ? ''
      : messageUtility.getMessage('E-000-041');
    stateValue.validErrorMessage.visual_acuity_right = _.isUndefined(
      result.messages.visual_acuity_right
    )
      ? ''
      : messageUtility.getMessage('E-000-041');
    stateValue.validErrorMessage.high_bp = _.isUndefined(
      result.messages.high_bp
    )
      ? ''
      : messageUtility.getMessage('E-000-017');

    stateValue.validErrorMessage.low_bp = _.isUndefined(result.messages.low_bp)
      ? ''
      : messageUtility.getMessage('E-000-017');

    stateValue.validErrorMessage.medical_examination_date_error = _.isUndefined(
      result.messages.medical_examination_date
    )
      ? ''
      : result.messages.medical_examination_date;

    stateValue.validErrorMessage.special_medical_examination_date = _.isUndefined(
      result.messages.special_medical_examination_date
    )
      ? ''
      : result.messages.special_medical_examination_date;

    // 受診する、しないで項目が隠れるもの
    const prefixes = [
      'pneumoconiosis',
      'asbestos',
      'organic_solvent',
      'specific_chemicals',
      'ionization',
      'vibration_disease',
      'high_pressure',
      'lead',
      'tetraalkyl_lead',
      'decontamination',
      'dental',
    ];

    for (const prefix of prefixes) {
      stateValue.validErrorMessage = this.resetErrorMessageStateByPrefix(
        prefix,
        stateValue.validErrorMessage,
        result.messages
      );
    }

    this.setState(stateValue);
    return result.status;
  };

  // 年月日の未入力時、nullや//などのパターンがある為、安全書類項目のバリデーションのみ//に統一する
  formatDateForSafetyDocSchema = date => {
    if (isNullOrUndefinedOrEmptyStr(date)) {
      return '//';
    }
    return date;
  };

  validateAllSafetyDocItem = () => {
    const medicalExaminations = _.cloneDeep(this.state.medicalExaminations);
    for (const item in medicalExaminations) {
      if (
        [
          'medical_examination_date',
          'special_medical_examination_date_pneumoconiosis',
          'special_medical_examination_date_asbestos',
          'special_medical_examination_date_organic_solvent',
          'special_medical_examination_date_specific_chemicals',
          'special_medical_examination_date_ionization',
          'special_medical_examination_date_vibration_disease',
          'special_medical_examination_date_high_pressure',
          'special_medical_examination_date_lead',
          'special_medical_examination_date_tetraalkyl_lead',
          'special_medical_examination_date_decontamination',
          'special_medical_examination_date_dental',
        ].includes(item)
      ) {
        medicalExaminations[item] = this.formatDateForSafetyDocSchema(
          medicalExaminations[item]
        );
      }
    }
    const schema = MedicalExaminationService.createSafetyDocItemSchema();
    const result = validationByJoi(schema, medicalExaminations);
    return result;
  };

  getSafetyDocItemErrorMessage = messages => {
    const errorPropertyList = [];
    for (let key of Object.keys(messages)) {
      let constName = undefined;
      switch (key) {
        case 'high_bp':
        case 'low_bp':
          if (
            errorPropertyList.includes(
              SAFETY_DOC_ITEM_VALIDATE.MEDICAL_EXAMINATION.BP.TEXT
            )
          ) {
            break;
          }
          constName = Object.values(
            SAFETY_DOC_ITEM_VALIDATE.MEDICAL_EXAMINATION
          ).find(constValue => constValue.KEY === 'bp');

          break;
        default:
          constName = Object.values(
            SAFETY_DOC_ITEM_VALIDATE.MEDICAL_EXAMINATION
          ).find(constValue => constValue.KEY === key);
          break;
      }
      if (!_.isUndefined(constName)) {
        errorPropertyList.push(constName.TEXT);
      }
    }
    return errorPropertyList;
  };

  showSafetyDocItemDialog = () => {
    this.setState({
      dialogStatus: DIALOG_STATUS.safetyDocItemErrorDialogEnabled.id,
    });
  };

  handlerUpdate = () => {
    const result = this.validateAllSafetyDocItem();
    if (result.status) {
      return this.updateMedicalExamination();
    }
    const errorPropertyList = this.getSafetyDocItemErrorMessage(
      result.messages
    );
    this.setState(
      {
        errorPropertyList,
      },
      () => {
        this.showSafetyDocItemDialog();
      }
    );
  };

  /**
   * =====================================================
   * フォーム周りの処理 - END
   * =====================================================
   */

  /**
   * CCUSから情報を取得するボタン・補足説明の表示
   */
  showFetchCcusDescription = () => {
    const REACT_APP_ZENDESK_URL = ENV_VARS.REACT_APP_ZENDESK_URL;
    if (this.props.pageStatus.ID === WORKER_PAGE_STATUS.EDIT.ID) {
      if (this.props.isAgencyRegister) {
        if (!_.has(this.props.agencyInfo, 'in_upstream_ccus_field_trees')) {
          return;
        } else {
          // 代行先の作業員
          const canFetchCcus =
            this.props.isAlreadyCcusConnect &&
            this.props.agencyInfo.in_upstream_ccus_field_trees &&
            this.props.isLinkedUser;
          //  BLDE_V2-50226 CCUS事業者IDは削除された場合に転記情報は表示されない
          if (canFetchCcus && !this.props.isCcusJigyousyaDeleted) {
            if (isNullOrUndefinedOrEmptyStr(this.props.linkedToCcusDate)) {
              return (
                <FetchCcusDescription
                  description={messageUtility.getMessage('I-000-161')}
                  onClick={() => this.props.callFetchCcusWorkersData()}
                />
              );
            } else {
              return (
                <FetchCcusDescription
                  description={messageUtility.getMessage('I-000-162')}
                  linkedToCcusDate={this.props.linkedToCcusDate}
                  onClick={() => this.props.callFetchCcusWorkersData()}
                />
              );
            }
          } else {
            return (
              // 代行先のCCUSに登録された詳細情報はCCUSの制限により取得できません。<br>Buildeeに直接入力を行ってください。
              <FetchCcusDescription
                description={
                  <span>
                    CCUSから情報を取得することができません。赤字の条件が満たされていません。
                    <br />
                    CCUSから代行先の情報を取得するには、下記の条件をすべて満たす必要があります。
                    <br />
                    <br />
                    <ul className='fetch-ccus-description-message__text-list'>
                      {/* BLDE_V2-50226 CCUS事業者IDは削除された場合に文言は赤色になる */}
                      {/* BLDE_V2-50623 代行先会社はCCUSと連携されているかどうかによって文言の色は変更する */}
                      <li
                        className={
                          this.props.isCcusJigyousyaConnected &&
                          this.props.isAlreadyCcusConnect &&
                          !this.props.isCcusJigyousyaDeleted
                            ? ''
                            : 'text--negative'
                        }
                      >
                        <span className='fetch-ccus-description-message__text'>
                          {messageUtility.getMessage('I-000-263')}
                        </span>
                      </li>
                      <li
                        className={
                          this.props.isLinkedUser ? '' : 'text--negative'
                        }
                      >
                        <span className='fetch-ccus-description-message__text'>
                          あなたのアカウントが代行先の連携ユーザーとして設定されている
                        </span>
                        {!this.props.isLinkedUser && (
                          <span>
                            （
                            <span
                              className='clickable-text'
                              onClick={() => {
                                masterSlideLoader.show('PrimeCcusPreview', {
                                  state: { id: this.props.companyId },
                                });
                              }}
                            >
                              こちら
                            </span>
                            から設定してください）
                          </span>
                        )}
                      </li>
                      <li
                        className={
                          this.props.agencyInfo.in_upstream_ccus_field_trees
                            ? ''
                            : 'text--negative'
                        }
                      >
                        <span className='fetch-ccus-description-message__text'>
                          施工体制のCCUS連携を行っているいずれかの現場で、代行先の会社があなたの会社の下位協力会社として登録されている
                        </span>
                        {!this.props.agencyInfo
                          .in_upstream_ccus_field_trees && (
                          <span>
                            （詳細は
                            <a
                              className='clickable-text'
                              href={`${getZendeskArticleUrl(
                                REACT_APP_ZENDESK_URL,
                                ZENDESK_LINK_IDS.CCUS_FIELD_TREES
                              )}`}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              こちら
                            </a>
                            をご確認ください。）
                          </span>
                        )}
                      </li>
                    </ul>
                  </span>
                }
                disabledCcusConnectionButton
              />
            );
          }
        }
      }
      //  BLDE_V2-50226 CCUS事業者IDは削除された場合に転記情報は表示されない
      if (
        (this.props.isAlreadyCcusConnect &&
          this.props.isCcusJigyousyaDeleted) ||
        !this.props.isCcusJigyousyaConnected
      ) {
        return null;
      }
      if (
        !this.props.isLinkedUser ||
        !this.props.ccusEngineerId ||
        !this.props.isAlreadyCcusConnect
      ) {
        return (
          <FetchCcusDescription
            disabledCcusConnectionButton
            description={
              <div>
                <p>
                  CCUSから情報を取得することができません。赤字の条件が満たされていません。
                </p>
                <p>
                  CCUSから情報を取得するには、下記の条件をすべて満たす必要があります。
                </p>
                <ul className='fetch-ccus-description-message__text-list'>
                  <li
                    className={!this.props.isLinkedUser ? 'text--negative' : ''}
                  >
                    <span className='fetch-ccus-description-message__text'>
                      あなたのアカウントが自社の連携ユーザーとして設定されている
                    </span>
                    {!this.props.isLinkedUser && (
                      <span>
                      （
                      <span
                        className='clickable-text'
                        onClick={() => {
                          masterSlideLoader.show('PrimeCcusPreview', {
                            state: { id: this.props.companyId },
                          });
                        }}
                      >
                        こちら
                      </span>
                        から設定してください）
                      </span>
                    )}
                  </li>
                  <li
                    className={
                      !this.props.ccusEngineerId ||
                      !this.props.isAlreadyCcusConnect
                        ? 'text--negative'
                        : ''
                    }
                  >
                    <span className='fetch-ccus-description-message__text'>
                      作業員の基本情報タブにCCUS技能者IDが登録されておりCCUS連携に成功している
                    </span>
                  </li>
                </ul>
                <br />
                <p>
                  作業員のCCUS連携の手順は
                  <a
                    className='clickable-text'
                    href={ZENDESK_URL_GUIDE.CCUS_WORKER_WARNING}
                    target='_blank'
                    rel='noreferrer noopener'
                  >
                    こちら
                  </a>
                  をご確認ください。
                </p>
              </div>
            }
          />
        );
      }
      if (isNullOrUndefinedOrEmptyStr(this.props.linkedToCcusDate)) {
        return (
          <FetchCcusDescription
            description={messageUtility.getMessage('I-000-161')}
            onClick={() => this.props.callFetchCcusWorkersData()}
          />
        );
      } else {
        return (
          <FetchCcusDescription
            description={messageUtility.getMessage('I-000-162')}
            linkedToCcusDate={this.props.linkedToCcusDate}
            onClick={() => this.props.callFetchCcusWorkersData()}
          />
        );
      }
    }
  };

  // 画面表示
  render() {
    // console.log('MedicalExamination.jsx render', this.state.validErrorMessage);
    return (
      <React.Fragment>
        <WorkerHeader
          LinkButtonObject={this.state.header.LinkButtonObject}
          buttonObject={this.state.header.buttonObject}
          errorMessagesCount={
            /* eslint-disable no-unused-vars */
            Object.entries(this.state.validErrorMessage)
              .filter(([key, value]) => key !== 'medical_examination_date')
              .filter(([key, value]) => value !== '').length
            /* eslint-enable no-unused-vars */
          }
        />
        <div className='contruction-detail-view__bar' />

        <MedicalExaminationForm
          ccusDescription={this.showFetchCcusDescription()} /* CCUSから情報を取得するボタン・補足説明 */
          events={this.events}
          messages={this.state.validErrorMessage}
          formValues={this.state.medicalExaminations}
          ccus_info={this.state.ccus_info}
          disabledAll={this.props.pageStatus.DISABLED_ALL}
        />

        {/* 更新失敗時のダイアログ */}
        <Information
          visible={
            this.state.dialogStatus ===
            DIALOG_STATUS.updateFailedDialogEnabled.id
          }
          title={messageUtility.getMessage('E-000-005')}
          message={this.state.informationModal.message}
          buttonText={messageUtility.getMessage('I-000-008')}
          onClose={this.closeDialog}
        />

        {/* 編集をやめる時のダイアログ */}
        <Confirmation
          visible={
            this.state.dialogStatus === DIALOG_STATUS.cancelEditDialogEnabled.id
          }
          title={messageUtility.getMessage('I-000-025')}
          confirmButtonText={messageUtility.getMessage('I-000-025')}
          cancelButtonText={messageUtility.getMessage('I-000-008')}
          message={messageUtility.getMessage('I-000-050')}
          isNegative
          onConfirm={() => this.moveToWorkerConfirm()}
          onCancel={() => this.closeDialog()}
        />

        {/* 作業員一覧へ戻る時のダイアログ */}
        <Confirmation
          visible={
            this.state.dialogStatus ===
            DIALOG_STATUS.leaveThisPageDialogEnabled.id
          }
          title={messageUtility.getMessage('I-000-051')}
          confirmButtonText={messageUtility.getMessage('I-000-053')}
          cancelButtonText={messageUtility.getMessage('I-000-008')}
          message={messageUtility.getMessage('I-000-052')}
          isNegative
          onConfirm={() => this.moveToWorkerList()}
          onCancel={() => this.closeDialog()}
        />

        {/* CCUS全て転記ダイアログ */}
        <Confirmation
          visible={
            this.state.dialogStatus ===
            DIALOG_STATUS.ccusInfoTranscriptionAgreementDialogEnabled.id
          }
          title={messageUtility.getMessage('I-000-164')}
          confirmButtonText={messageUtility.getMessage('I-000-165')}
          cancelButtonText={messageUtility.getMessage('I-000-008')}
          message={
            <React.Fragment>
              {messageUtility.getMessage('I-000-166')}
              <MoveLeftIcon className='form-list__element-ccus-icon' />
              {messageUtility.getMessage('I-000-167')}
            </React.Fragment>
          }
          isNegative
          negativeButtonClassName='btn'
          rootClassName={styles.CcusInfoTranscriptionAgreementDialog}
          onConfirm={() => this.closeCcusInfoTranscriptionAgreementDialog()}
          onCancel={() => this.closeDialog()}
        />

        <ConfirmationSafetyDocItemDialog
          visible={
            this.state.dialogStatus ===
            DIALOG_STATUS.safetyDocItemErrorDialogEnabled.id
          }
          errorPropertyList={this.state.errorPropertyList}
          onConfirm={() => {
            this.setState({ dialogStatus: DIALOG_STATUS.disabled.id }, () => {
              this.updateMedicalExamination();
            });
          }}
          onCancel={() => {
            this.setState({ dialogStatus: DIALOG_STATUS.disabled.id });
          }}
        />
      </React.Fragment>
    );
  }
}

// 定数
const data = {
  buttonLink: {
    label: '作業員一覧',
    defaultClassName: 'btn-text--sm',
  },
  defaultButton: {
    label: '',
    className: 'btn',
  },
  header: {
    confirm: {
      button: {
        edit: {
          label: '編集',
          className: 'btn',
        },
      },
    },
    edit: {
      button: {
        cancel: {
          label: '編集をやめる',
          className: 'btn--sub',
        },
        update: {
          label: '更新',
          className: 'btn--effective',
        },
      },
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/worker/page/MedicalExamination.jsx